// packages/web/src/hooks/useAddressValidation.ts
// Hook for managing address validation and warnings

import { useState, useEffect } from 'react';

import { useAddress } from '../contexts/AddressContext';
import { MarketplaceService } from '../types/pricing';

interface AddressWarningState {
  open: boolean;
  newAddress: any;
  serviceAvailable: boolean;
}

interface UseAddressValidationResult {
  addressWarningDialog: AddressWarningState;
  setAddressWarningDialog: (state: AddressWarningState) => void;
  handleAddressWarningConfirm: () => void;
  handleAddressWarningCancel: () => void;
}

export const useAddressValidation = (
  service: MarketplaceService | null,
  router: any,
): UseAddressValidationResult => {
  const { selectedAddress } = useAddress();
  const [addressWarningDialog, setAddressWarningDialog] = useState<AddressWarningState>({
    open: false,
    newAddress: null,
    serviceAvailable: false,
  });

  // Listen for address changes and check service availability
  useEffect(() => {
    const handleAddressChange = (event: CustomEvent) => {
      const newAddress = event.detail?.address;
      if (service && newAddress && selectedAddress) {
        const serviceAvailable = checkServiceAvailability(service, newAddress);

        if (!serviceAvailable) {
          setAddressWarningDialog({
            open: true,
            newAddress,
            serviceAvailable: false,
          });
        }
      }
    };

    window.addEventListener('addressChanged', handleAddressChange as EventListener);

    return () => {
      window.removeEventListener('addressChanged', handleAddressChange as EventListener);
    };
  }, [service, selectedAddress]);

  const checkServiceAvailability = (service: MarketplaceService, address: any): boolean => {
    if (!service.geographic_restrictions) return true;

    const restrictions = service.geographic_restrictions;
    if (restrictions.type === 'everywhere') return true;

    const userState = address.state || '';
    const userZip = address.zip_code || '';

    switch (restrictions.type) {
      case 'states':
        return restrictions.locations?.includes(userState) || false;

      case 'zip_codes':
        return restrictions.locations?.includes(userZip) || false;

      case 'counties':
        return (
          restrictions.locations?.some((county) =>
            county.toLowerCase().includes(userState.toLowerCase()),
          ) || false
        );

      case 'combined':
        return (
          restrictions.locations?.some((location) => {
            const normalizedLocation = location.toLowerCase();
            const normalizedState = userState.toLowerCase();
            return normalizedLocation.includes(normalizedState) || location === userZip;
          }) || false
        );

      default:
        return true;
    }
  };

  const handleAddressWarningConfirm = () => {
    setAddressWarningDialog({ open: false, newAddress: null, serviceAvailable: false });
    router.push('/marketplace');
  };

  const handleAddressWarningCancel = () => {
    setAddressWarningDialog({ open: false, newAddress: null, serviceAvailable: false });
  };

  return {
    addressWarningDialog,
    setAddressWarningDialog,
    handleAddressWarningConfirm,
    handleAddressWarningCancel,
  };
};
